import {AfterViewInit, Component, ElementRef, EventEmitter, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {
  IListEditorTemplateComponent
} from '@shared/components/platform-tool/platform-tool-dialog-definition/models/IListEditorTemplateComponent';
import {ITileViewSelection, IVirtualGridTile} from '@shared/interfaces/virtual-grid-tile';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {PlatformCommand} from '@shared/commands/command-base';
import {Api} from '@shared/constants/common-field.constants';
import {ScenariosHandler} from '@features/scenarios-core/services/handlers/scenarios-handler.service';

import {UiHelper} from "@shared/models/helpers/UiHelper";
import {CommandService} from '@core/services/command.service';
import {Configuration} from '@shared/models/configuration';
import {TxpCountryCodes} from '@features/global-tax-calculator/models/txp.constants';

@Component({
    standalone: false,
    selector: 'app-gtc-scenarios-tile-list',
  templateUrl: './gtc-scenarios-tile-list.component.html',
  styleUrls: ['./gtc-scenarios-tile-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class GtcScenariosTileListComponent implements OnInit, IListEditorTemplateComponent, IVirtualGridTile, AfterViewInit {
  // Hidden inside a workspace: it holds one scenario whose compliance flag and permissions come from the parent.
  private static readonly actionsHiddenInsideAWorkspace = ['Duplicate', 'RollForward', 'Delete', 'Archive', 'UnArchive',
    'ToMaster', 'ToNormal', 'ManagePermissions', 'RequestAccess'];
  action: EventEmitter<any>;
  data: any;
  inputMetadata: any;
  invalidateSubject: InvalidateSubject;
  itemSelectionChanged: EventEmitter<ITileViewSelection>;
  title: string;
  entityCount: number;
  transactionCount: number;
  valueChanged: EventEmitter<any>;
  currentScenarioType: string;
  isShowCreateFilter: boolean;
  isWorkspaceScenario = false;
  isInsideWorkspace = false;
  quarter: string;
  tags: string[];
  versionTooltips: {
    [key: string]: string;
  };
  tagClassMap = {
    ScheduleUpdates: 'scheduled'
  };
  actionTags: string[];
  isLocked: boolean;
  isIncomplete: boolean;
  lockedScenarioMessage = `<div class="lock-tooltip-container"><h2>Locked Scenario</h2>
  <p style="color:#B7BFCC">This scenario has been locked, which prevents further edits and data inputs. Open this scenario to view the contents and to generate additional reports.</p></div>`;
  templateUpdateChoices: any;
  private isTemplateUpdateAvailable = {};
  private isCountryTrackerUpdateAvailable = {};
  scenarioNameEllipsisActivated = false;
  isPermissionFeatureEnabled = false;
  isLegacyTemplate = false;
  @ViewChild('scenarioNameRef', {static: true}) scenarioNameRef: ElementRef;

  constructor(
    private handler: ScenariosHandler,
    private commandService: CommandService
  ) { }

  ngOnInit(): void {
    console.log(this.data);
    this.isPermissionFeatureEnabled = Configuration.getConfig('EnablePermissionFeature');
    this.isInsideWorkspace = !!Configuration.getConfig('IsWorkspace');
    this.currentScenarioType = this.data.cells['CurrentScenarioType']?.value;
    this.isShowCreateFilter = this.getIsShowCreateFilter(this.currentScenarioType,this.data.cells['ScenarioType']?.value);
    this.quarter = this.data.cells['Quarter']?.displayText;
    this.tags = this.data.cells['ScenarioType']?.value;
    this.actionTags = this.data.cells['Tag']?.value.split(',');
    this.isLocked = this.data.cells['IsLocked']?.value;
    this.isIncomplete = this.data.cells['IsIncomplete']?.value;
    this.versionTooltips = this.data.cells['VersionTooltips']?.value;
    this.templateUpdateChoices = {
      'GMT': this.data.cells[`UpdateChoice-GMT`]?.value,
      'ITC': this.data.cells[`UpdateChoice-ITC`]?.value,
      'CbC': this.data.cells[`UpdateChoice-CbC`]?.value
    };

    this.SetTemplateUpdateInfo();
    this.SetCountryTrackerUpdateInfo();
    if (this.data.cells['IsWorkspaceScenario']) {
      this.isWorkspaceScenario = true;
    }
    // this.entityCount = this.data.cells['ScenarioEntitiesCount']?.value ?? 0;
    // this.transactionCount = this.data.cells['ScenarioTransactionsCount']?.value ?? 0;
  }

  private SetCountryTrackerUpdateInfo() {
    if (this.data.cells[`${this.currentScenarioType}-UpdatedCountryTrackerVersion`] && this.data.cells[`${this.currentScenarioType}-CurrentCountryTrackerVersion`]) {
      this.isCountryTrackerUpdateAvailable[this.currentScenarioType] = this.data.cells[`${this.currentScenarioType}-UpdatedCountryTrackerVersion`].value !== this.data.cells[`${this.currentScenarioType}-CurrentCountryTrackerVersion`].value;
    }
  }

  private SetTemplateUpdateInfo() {
    if (this.data.cells[`${this.currentScenarioType}-UpdatedVersion`] && this.data.cells[`${this.currentScenarioType}-CurrentVersion`]) {
      let isLegacyTemplate = this.data.cells[`Is${this.currentScenarioType}LegacyTemplate`]
      if (isLegacyTemplate.value) {
        this.isLegacyTemplate = true;
        this.isTemplateUpdateAvailable[this.currentScenarioType] = false
      } else {
        this.isTemplateUpdateAvailable[this.currentScenarioType] = this.data.cells[`${this.currentScenarioType}-UpdatedVersion`].value !== this.data.cells[`${this.currentScenarioType}-CurrentVersion`].value;
      }
    }
    TxpCountryCodes.forEach(subType => {
      const updatedKey = `${this.currentScenarioType}-${subType}-UpdatedVersion`;
      const currentKey = `${this.currentScenarioType}-${subType}-CurrentVersion`;
      if (this.data.cells[updatedKey] && this.data.cells[currentKey]) {
        const hasUpdate = this.data.cells[updatedKey].value !== this.data.cells[currentKey].value;
        this.isTemplateUpdateAvailable[`${this.currentScenarioType}-${subType}`] = hasUpdate;
        if (hasUpdate) {
          this.isTemplateUpdateAvailable[this.currentScenarioType] = true;
        }
      }
    });
  }

  // The parent's view of a workspace's scenarios keeps no menu; inside the workspace its own scenario has one.
  isMenuShown(): boolean {
    return !this.isWorkspaceScenario || this.isInsideWorkspace;
  }

  isActionShown(action: string): boolean {
    return !this.isInsideWorkspace || !GtcScenariosTileListComponent.actionsHiddenInsideAWorkspace.includes(action);
  }

  // A fresh workspace copy has no saved update choice, so inside a workspace an available update is enough.
  isManageUpdatesShown(): boolean {
    const isUpdateAvailable = !!(this.isTemplateUpdateAvailable[this.currentScenarioType]
      || this.isCountryTrackerUpdateAvailable[this.currentScenarioType]);
    return isUpdateAvailable && (!!this.data.cells['UpdateChoice-' + this.currentScenarioType] || this.isInsideWorkspace);
  }

  getIsShowCreateFilter(solutionType: string, iconScenarioTypes: string[]): boolean {
    if(!iconScenarioTypes?.includes(solutionType)) return false;
    switch (solutionType?.toLowerCase()) {
      case 'gmt':
        return true;
      case 'txp':
        return true;
      default:
        return false;
    }
    return false;
  }


  onClick($event: any) {
    const command = this.handler?.magicLinkModel.additionalCommands['ScenarioName'];
    if (command) {
      command.fieldValues['ScenarioId'] = this.data.id;
      this.handler.executeCommand(command);
    }
  }

  onButtonClick($event: any) {
    $event.stopPropagation();
  }

  onShowActivityButtonClick($event: any) {
    //$event.stopPropagation();
    this.handler.showActivity(this.data);
  }

  onActionClick(action: string) {
    switch (action){
      case 'Archive':
      case 'UnArchive':
        this.handler.archiveScenario(this.data, action === 'Archive');
        break;
      case 'Delete':
        this.handler.deleteScenario(this.data);
        break;
      case 'Duplicate':
        this.handler.duplicateScenario(this.data);
        break;
      case 'RollForward':
        this.handler.rollForwardScenario(this.data);
        break;
      case 'CreateSupportTicket':
        this.handler.createSupportTicket(this.data);
        break;
      case 'ManageUpdate':
        this.handler.manageUpdate(this.data);
        break;
      case 'ToMaster':
        this.handler.ToMaster(this.data);
        break;
      case 'ToNormal':
        this.handler.ToNormal(this.data);
        break;
      case 'ShowSnapshots':
        this.handler.onShowSnapshotButtonClick(this.data);
        break;
      case 'Unlock':
        this.handler.onUnlockScenario(this.data);
        break;
      case 'Lock':
        this.handler.onLockScenario(this.data);
        break;
      case 'ManagePermissions':
        this.handler.onManagePermissions(this.data);
        break;
      case 'ScenarioSettings':
        this.handler.onScenarioSetting(this.data);
        break;
      case 'ApplyFieldSchema':
        this.handler.onApplySchema(this.data);
        break;
    }
  }

  onTagClick($event: any, tag: string) {
    $event.stopPropagation();
    const command = this.handler?.magicLinkModel.additionalCommands['ScenarioName'];
    if (command) {
      command.fieldValues['ScenarioId'] = this.data.id;
      command.fieldValues['ScenarioType'] = this.GetScenarioTypeFromTag(tag);
      this.handler.executeCommand(command);
    }

  }

  GetScenarioTypeFromTag(tag: string) {
    switch (tag) {
      case 'CBC':
        return 'CbC';
      default:
        return tag;
    }
  }

  // onUnlockScenario() {
  //   if(this.isScheduledScenario(this.currentScenarioType)){
  //     this.handler.manageUpdate(this.data);
  //     return;
  //   }
  //   const command = this.handler.magicLinkModel.additionalCommands['UnlockScenarioCommand'] as PlatformCommand;
  //   command.fieldValues['ScenarioId'] = this.data.id;
  //   this.handler.executeCommand(command);
  // }
  // onShowSnapshotButtonClick() {
  //   const command = this.handler.magicLinkModel.additionalCommands['CreateSnapshotToolLinkCommand'] as PlatformCommand;
  //   command.fieldValues['ScenarioId'] = this.data.id;
  //   this.handler.executeCommand(command);
  // }
  //
  //
  // onLockScenario() {
  //   const command = this.handler?.magicLinkModel.additionalCommands['LockScenarioCommand'] as PlatformCommand;
  //   command.fieldValues['ScenarioId'] = this.data.id;
  //   this.handler.executeCommand(command);
  // }



  isScheduledScenario(scenarioType){
    if(this.templateUpdateChoices[scenarioType]){
      return this.templateUpdateChoices[scenarioType].updateSettings === 'ScheduleUpdates';
    }
    return false;
  }
  // isScheduledScenario(scenarioType){
  //   if(this.templateUpdateChoices[scenarioType]){
  //     return this.templateUpdateChoices[scenarioType].updateSettings === 'ScheduleUpdates';
  //   }
  //   return false;
  // }
  ngAfterViewInit(): void{
    if(UiHelper.isWebkitBoxElementEllipsisActive(this.scenarioNameRef.nativeElement, 2)){
      this.scenarioNameEllipsisActivated = true;
    }
  }

  // onManagePermissions() {
  //   const managePermissionCommand = new PlatformCommand('ManagePermissionDialogCommand', Api.Security);
  //   managePermissionCommand.waitForResponse = true;
  //
  //   managePermissionCommand.fieldValues['DataPointId'] = this.data.id;
  //   managePermissionCommand.fieldValues['FeatureId'] = this.currentScenarioType;
  //   this.commandService.executePlatformCommand(managePermissionCommand);
  // }

  onAccessRequest() {
    const requestAccessCommand = new PlatformCommand('RequestAccessDialogCommand', Api.Security);
    requestAccessCommand.waitForResponse = true;

    requestAccessCommand.fieldValues['DataPointId'] = this.data.id;
    requestAccessCommand.fieldValues['FeatureId'] = this.currentScenarioType;
    requestAccessCommand.fieldValues['DataPointName'] = this.data.cells?.ScenarioName?.displayText;

    this.commandService.executePlatformCommand(requestAccessCommand);
  }

  isMasterScenario(scenarioType: string): boolean {
    return !!this.data.cells['Is' + scenarioType + 'MasterScenario']?.value;
  }

  convertToMasterText(scenarioType:string): string{
    let title = scenarioType == 'GMT' ? 'Compliance' : 'Master';
     return 'Assign '+title+' Scenario';
  }

  convertToNormalText(scenarioType:string): string{
    let title = scenarioType == 'GMT' ? 'Compliance' : 'Master';
     return 'Unassign '+title+' Scenario';
  }
  onAddFilterClick($event: any) {

    var platformCommand = new PlatformCommand("AddOrUpdateScenarioFilterCommand",Api.Spreadsheet);
    platformCommand.fieldValues["data"] = this.data;
    platformCommand.fieldValues['ScenarioId'] = this.data.id;
    platformCommand.fieldValues['ScenarioType'] = this.currentScenarioType;
    this.handler.executeCommand(platformCommand);
  }

  formatDate(displayText: string): string {
    if (!displayText) return '';
    const date = new Date(displayText);
    if (isNaN(date.getTime())) return displayText;
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }
}
